import { ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { memo, useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useFormatDate } from '../../hooks/useFormatDate';
import { ToastType, useToastNotifications } from '../../hooks/useToastNotifications';
import { T, useT } from '../../translation/src';
import { FailedPointCloudStatus, SuperadminFailedPointCloudsQuery, useSuperadminFailedPointCloudCountsQuery, useSuperadminFailedPointCloudsQuery } from '../../types/graphqlTypes';
import { AdminListPage, AdminListTab, StatusPill, StatusTone } from './AdminListPage';

const PAGE_SIZE = 50;

type Tab = FailedPointCloudStatus | 'ALL';
type FailedPointCloudRow = SuperadminFailedPointCloudsQuery['superadminFailedPointClouds']['data'][number];

const STATUS_TONE: Record<FailedPointCloudStatus, StatusTone> = {
  [FailedPointCloudStatus.Failed]: 'red',
  [FailedPointCloudStatus.Resolved]: 'green',
  [FailedPointCloudStatus.Deleted]: 'orange',
};

const formatSize = (bytes?: number | null): string => {
  if (!bytes) return '-';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const exponent = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  return `${(bytes / 1024 ** exponent).toFixed(exponent === 0 ? 0 : 1)} ${units[exponent]}`;
};

// A record for a project that failed as a whole (no point cloud to pin it on) has this cloud name.
const WHOLE_PROJECT_CLOUD_NAME = '*';

// The file as the user named it: the display name, else the cloud name without its upload id prefix.
const fileLabel = (row: FailedPointCloudRow): string => row.displayName || row.cloudName.slice(row.cloudName.indexOf('___') + 3);

const useStatusLabels = (): Record<FailedPointCloudStatus, string> => {
  const failed = useT('needs attention', { swc: true });
  const resolved = useT('resolved', { swc: true });
  const deleted = useT('deleted', { swc: true });
  return { [FailedPointCloudStatus.Failed]: failed, [FailedPointCloudStatus.Resolved]: resolved, [FailedPointCloudStatus.Deleted]: deleted };
};

// Superadmins: every point cloud whose conversion failed, from the failure until it is converted after
// all (green, resolved) or deleted (orange). The red ones need attention.
const FailedUploads_: React.FC = () => {
  const [tab, setTab] = useState<Tab>(FailedPointCloudStatus.Failed);
  const statusLabels = useStatusLabels();
  const { formatDateAndTime } = useFormatDate();
  const { showToast } = useToastNotifications();
  const copied = useT('copied', { swc: true });
  const wholeProject = useT('whole project', { swc: true });

  const status = tab === 'ALL' ? undefined : tab;
  const { data, loading, fetchMore } = useSuperadminFailedPointCloudsQuery({ variables: { status, pagination: { limit: PAGE_SIZE } }, fetchPolicy: 'cache-and-network' });
  const { data: countsData } = useSuperadminFailedPointCloudCountsQuery({ fetchPolicy: 'cache-and-network' });
  const counts = countsData?.superadminFailedPointCloudCounts;

  const page = data?.superadminFailedPointClouds;
  const rows = page?.data ?? [];
  const nextCursor = page?.nextCursor;
  const onLoadMore = useCallback(() => {
    if (!nextCursor) return;
    void fetchMore({
      variables: { status, pagination: { limit: PAGE_SIZE, cursor: nextCursor } },
      updateQuery: (previous, { fetchMoreResult }) => ({
        superadminFailedPointClouds: { ...fetchMoreResult.superadminFailedPointClouds, data: [...previous.superadminFailedPointClouds.data, ...fetchMoreResult.superadminFailedPointClouds.data] },
      }),
    });
  }, [fetchMore, nextCursor, status]);

  const copy = useCallback(
    async (text: string) => {
      try {
        await navigator.clipboard.writeText(text);
        showToast({ title: copied, text, type: ToastType.SUCCESS });
      } catch {
        // Clipboard refused (no focus / permission): nothing to report beyond not copying.
      }
    },
    [copied, showToast],
  );

  const tabs = useMemo<AdminListTab<Tab>[]>(
    () => [
      { key: FailedPointCloudStatus.Failed, label: statusLabels[FailedPointCloudStatus.Failed], count: counts?.failed, tone: 'red' },
      { key: FailedPointCloudStatus.Resolved, label: statusLabels[FailedPointCloudStatus.Resolved], count: counts?.resolved, tone: 'green' },
      { key: FailedPointCloudStatus.Deleted, label: statusLabels[FailedPointCloudStatus.Deleted], count: counts?.deleted, tone: 'orange' },
      { key: 'ALL', label: <T _str="all" swc />, count: counts ? counts.failed + counts.resolved + counts.deleted : undefined },
    ],
    [counts, statusLabels],
  );

  return (
    <AdminListPage
      title={<T _str="failed uploads" swc />}
      tabs={tabs}
      activeTab={tab}
      onTabChange={setTab}
      columns={[<T _str="status" swc />, <T _str="file" swc />, <T _str="project" swc />, <T _str="organisation" swc />, <T _str="size" swc />, <T _str="failures" swc />, <T _str="last failed" swc />, <T _str="closed" swc />, '']}
      isEmpty={rows.length === 0}
      emptyText={<T _str="nothing here" swc />}
      loading={loading}
      hasMore={!!page?.hasMore}
      onLoadMore={onLoadMore}
      rows={rows.map((row) => (
        <tr key={row.id} className="border-b border-gray-200 dark:border-gray-700 align-top">
          <td className="py-2 pr-4">
            <StatusPill tone={STATUS_TONE[row.status]}>{statusLabels[row.status]}</StatusPill>
          </td>
          <td className="py-2 pr-4 max-w-72" title={row.fileNames.join('\n')}>
            <div className="truncate">{row.cloudName === WHOLE_PROJECT_CLOUD_NAME ? <span className="italic text-gray-500">{wholeProject}</span> : fileLabel(row)}</div>
            {row.lastError && (
              <div className="text-xs text-gray-500 truncate" title={row.lastError}>
                {row.lastError}
              </div>
            )}
          </td>
          <td className="py-2 pr-4">
            {/* A deleted project has nowhere to go; a live one opens its organisation's project list. */}
            {row.status !== FailedPointCloudStatus.Deleted && row.organisationId ? (
              <Link to={`/organisations/${row.organisationId}/projects`} className="underline hover:text-neon-green-700 dark:hover:text-neon-green-300">
                {row.projectName || row.projectId}
              </Link>
            ) : (
              row.projectName || row.projectId
            )}
          </td>
          <td className="py-2 pr-4">{row.organisationName || '-'}</td>
          <td className="py-2 pr-4 whitespace-nowrap">{formatSize(row.sizeBytes)}</td>
          <td className="py-2 pr-4">{row.failureCount}</td>
          <td className="py-2 pr-4 whitespace-nowrap">{formatDateAndTime(row.lastFailedAt)}</td>
          <td className="py-2 pr-4 whitespace-nowrap">{row.resolvedAt ? formatDateAndTime(row.resolvedAt) : row.deletedAt ? formatDateAndTime(row.deletedAt) : '-'}</td>
          <td className="py-2 whitespace-nowrap text-xs text-gray-500">
            <button type="button" onClick={() => copy(row.projectId)} className="inline-flex items-center gap-1 underline hover:text-black dark:hover:text-white">
              <ClipboardDocumentIcon className="w-3.5 h-3.5" />
              <T _str="project ID" />
            </button>
            {row.lastJobId && (
              <button type="button" onClick={() => copy(row.lastJobId ?? '')} className="ml-3 inline-flex items-center gap-1 underline hover:text-black dark:hover:text-white">
                <ClipboardDocumentIcon className="w-3.5 h-3.5" />
                <T _str="job" />
              </button>
            )}
          </td>
        </tr>
      ))}
    />
  );
};

export const FailedUploads = memo(FailedUploads_);
